// ============================================================
// Page Taxes — Lot L17 du plan SaaS (frontend)
// ============================================================
// CRUD taxes + paramètre global prix_inclut_taxes
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getDeviseCourante } from '../../utils/format';
import {
  Receipt, Plus, X, Loader, Edit2, ToggleRight, ToggleLeft, Settings,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function PageTaxes() {
  const { canEditParametres } = useAuth();
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [prixInclutTaxes, setPrixInclutTaxes] = useState(false);
  const [savingParam, setSavingParam] = useState(false);

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/taxes');
      setTaxes(data.taxes || data || []);
      if (data.commerce && typeof data.commerce.prix_inclut_taxes === 'boolean') {
        setPrixInclutTaxes(data.commerce.prix_inclut_taxes);
      }
    } catch { toast.error('Échec'); }
    finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, []);

  const enregistrer = async (f) => {
    try {
      const payload = {
        code: f.code, nom: f.nom,
        taux: parseFloat(f.taux) || 0,
        type: f.type || 'pourcentage',
      };
      if (f.id) await api.put(`/taxes/${f.id}`, payload);
      else      await api.post('/taxes', payload);
      toast.success('Taxe enregistrée');
      setModal(null); charger();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };
  const toggle = async (t) => {
    try {
      await api.patch(`/taxes/${t.id}/statut`, { actif: !t.actif });
      charger();
    } catch { toast.error('Échec'); }
  };
  const sauverParametre = async () => {
    setSavingParam(true);
    try {
      await api.patch('/taxes/commerce/par-defaut', { prix_inclut_taxes: prixInclutTaxes });
      toast.success('Paramètre enregistré');
    } catch { toast.error('Échec'); }
    finally { setSavingParam(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <Receipt size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Taxes & TVA</h1>
            <p className="text-xs text-gray-500">Paramétrage des taxes appliquées aux ventes</p>
          </div>
        </div>
        {canEditParametres && (
          <button onClick={() => setModal({ code: '', nom: '', taux: 0, type: 'pourcentage' })}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouvelle taxe
          </button>
        )}
      </header>

      {/* Paramètre global */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Settings size={16} className="text-gray-400" />
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Les prix saisis incluent-ils les taxes ?</p>
            <p className="text-xs text-gray-500">
              Si oui (HT inclus), les taxes sont retro-calculées. Sinon, elles sont ajoutées au prix saisi.
            </p>
          </div>
          {canEditParametres ? (
            <button onClick={() => setPrixInclutTaxes(p => !p)}
              className="flex items-center gap-2 text-sm">
              {prixInclutTaxes ?
                <><ToggleRight size={22} className="text-emerald-400" /> <span className="text-emerald-400">Oui (TTC)</span></> :
                <><ToggleLeft size={22} className="text-gray-500" /> <span className="text-gray-400">Non (HT + taxe)</span></>
              }
            </button>
          ) : (
            <span className="text-sm text-gray-400">{prixInclutTaxes ? 'TTC' : 'HT + taxe'}</span>
          )}
        </div>
        {canEditParametres && (
          <button onClick={sauverParametre} disabled={savingParam}
            className="mt-3 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white text-sm rounded">
            {savingParam ? 'Enregistrement…' : 'Enregistrer le paramètre'}
          </button>
        )}
      </div>

      {/* Tableau des taxes */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Nom</th>
              <th className="px-3 py-2 text-right">Taux</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">État</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8"><Loader size={20} className="animate-spin mx-auto text-gray-500" /></td></tr>
            ) : taxes.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8">Aucune taxe paramétrée</td></tr>
            ) : taxes.map(t => (
              <tr key={t.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                <td className="px-3 py-2 font-mono text-xs text-gray-300">{t.code}</td>
                <td className="px-3 py-2 text-gray-300">{t.nom}</td>
                <td className="px-3 py-2 text-right text-yellow-400 tabular-nums">
                  {t.taux}{t.type === 'pourcentage' ? ' %' : ' ' + getDeviseCourante()}
                </td>
                <td className="px-3 py-2 text-gray-400 text-xs">{t.type}</td>
                <td className="px-3 py-2">
                  {canEditParametres ? (
                    <button onClick={() => toggle(t)} className="text-gray-400 hover:text-white">
                      {t.actif ? <ToggleRight size={18} className="text-emerald-400" /> : <ToggleLeft size={18} />}
                    </button>
                  ) : (t.actif ? <span className="text-emerald-400 text-xs">Actif</span> : <span className="text-gray-500 text-xs">Inactif</span>)}
                </td>
                <td className="px-3 py-2 text-right">
                  {canEditParametres && (
                    <button onClick={() => setModal(t)} className="text-gray-400 hover:text-white"><Edit2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{modal.id ? 'Modifier' : 'Nouvelle'} taxe</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={modal.code || ''}
                onChange={e => setModal({ ...modal, code: e.target.value })}
                placeholder="Code (ex: TVA18)"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              <input type="text" value={modal.nom || ''}
                onChange={e => setModal({ ...modal, nom: e.target.value })}
                placeholder="Nom complet"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" min="0" step="0.5" value={modal.taux || 0}
                  onChange={e => setModal({ ...modal, taux: e.target.value })}
                  placeholder="Taux"
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white tabular-nums" />
                <select value={modal.type || 'pourcentage'}
                  onChange={e => setModal({ ...modal, type: e.target.value })}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                  <option value="pourcentage">Pourcentage</option>
                  <option value="fixe">Montant fixe</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
              <button onClick={() => enregistrer(modal)}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
